import { useState } from 'react';
import { LeadForm } from '../components/shared/LeadForm';
import { BookingModal } from '../components/shared/BookingModal';
import { Button } from '../components/ui/button';
import { Mail, Phone, MapPin, Clock, MessageCircle } from 'lucide-react';

export default function ContactPage() {
    const [bookingOpen, setBookingOpen] = useState(false);

    return (
        <div className="relative" data-testid="contact-page">
            {/* Hero */}
            <section className="section-padding bg-muted/30">
                <div className="container-custom">
                    <div className="max-w-4xl">
                        <p className="text-sm font-medium tracking-wider uppercase text-primary mb-4">
                            Contact Us
                        </p>
                        <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
                            Let's Talk Growth
                        </h1>
                        <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                            Ready to dominate search? Get in touch for a free strategy consultation 
                            and see how we can grow your business.
                        </p>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section className="section-padding">
                <div className="container-custom">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                        {/* Contact Info */}
                        <div>
                            <h2 className="text-2xl font-semibold mb-8">Get in Touch</h2>
                            
                            <div className="space-y-6 mb-12">
                                <a 
                                    href="mailto:hello@aitrixy.com"
                                    className="flex items-start gap-4 p-4 rounded-2xl border border-border hover:border-primary/30 bg-background transition-colors group"
                                >
                                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                                        <Mail className="w-6 h-6 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1">Email</h3>
                                        <p className="text-muted-foreground">hello@aitrixy.com</p>
                                        <p className="text-sm text-muted-foreground mt-1">We respond within 24 hours</p>
                                    </div>
                                </a>

                                <a 
                                    href="tel:+919876543210"
                                    className="flex items-start gap-4 p-4 rounded-2xl border border-border hover:border-primary/30 bg-background transition-colors group"
                                >
                                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                                        <Phone className="w-6 h-6 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1">Phone</h3>
                                        <p className="text-muted-foreground">+91 98765 43210</p>
                                        <p className="text-sm text-muted-foreground mt-1">Mon-Fri, 10am-6pm IST</p>
                                    </div>
                                </a>

                                <a 
                                    href="https://wa.me/919876543210"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-start gap-4 p-4 rounded-2xl border border-border hover:border-primary/30 bg-background transition-colors group"
                                >
                                    <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-green-500/20 transition-colors">
                                        <MessageCircle className="w-6 h-6 text-green-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1">WhatsApp</h3>
                                        <p className="text-muted-foreground">Quick response on WhatsApp</p>
                                        <p className="text-sm text-muted-foreground mt-1">Typically replies in minutes</p>
                                    </div>
                                </a>

                                <div className="flex items-start gap-4 p-4 rounded-2xl border border-border bg-background">
                                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                                        <MapPin className="w-6 h-6 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1">Office</h3>
                                        <p className="text-muted-foreground">New Delhi, India</p>
                                        <p className="text-sm text-muted-foreground mt-1">By appointment only</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4 p-4 rounded-2xl border border-border bg-background">
                                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                                        <Clock className="w-6 h-6 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1">Business Hours</h3>
                                        <p className="text-muted-foreground">Monday - Friday: 10am - 6pm IST</p>
                                        <p className="text-sm text-muted-foreground mt-1">Closed on weekends</p>
                                    </div>
                                </div>
                            </div>

                            {/* Quick Booking */}
                            <div className="p-6 rounded-2xl bg-foreground text-background">
                                <h3 className="text-xl font-semibold mb-3">Prefer a Call?</h3>
                                <p className="text-background/70 mb-4">
                                    Schedule a free 30-minute strategy call with our team.
                                </p>
                                <Button 
                                    className="bg-background text-foreground hover:bg-background/90 rounded-full"
                                    onClick={() => setBookingOpen(true)}
                                    data-testid="contact-booking-btn"
                                >
                                    Book Strategy Call
                                </Button>
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div>
                            <div className="p-8 rounded-3xl border border-border bg-background">
                                <h2 className="text-2xl font-semibold mb-2">Send Us a Message</h2>
                                <p className="text-muted-foreground mb-8">
                                    Fill out the form below and we'll get back to you within 24 hours.
                                </p>
                                <LeadForm sourcePage="contact" showServiceSelect />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Preview */}
            <section className="section-padding bg-muted/30">
                <div className="container-custom">
                    <div className="max-w-3xl mx-auto text-center">
                        <h2 className="text-3xl font-semibold tracking-tight mb-6">
                            Common Questions
                        </h2>
                        <div className="space-y-4 text-left">
                            {[
                                {
                                    q: 'How long does it take to see results?',
                                    a: 'Most clients see initial improvements within 2-3 months, with significant results in 4-6 months.',
                                },
                                {
                                    q: 'Do you work with all industries?',
                                    a: 'We specialize in healthcare, travel, visa agencies, and local businesses, but we work with any business that can benefit from search visibility.',
                                },
                                {
                                    q: 'What\'s your pricing?',
                                    a: 'Our engagements typically range from ₹50K to ₹3L per month depending on scope. We\'ll provide a custom quote after understanding your needs.',
                                },
                            ].map((faq, index) => (
                                <div 
                                    key={index}
                                    className="p-6 rounded-2xl border border-border bg-background"
                                >
                                    <h3 className="font-semibold mb-2">{faq.q}</h3>
                                    <p className="text-muted-foreground">{faq.a}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <BookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
        </div>
    );
}
